import { ReportSignature, SignatureType } from '@/types/transmissionReport';

export type SignatureFlow = 'one_sign' | 'two_sign';

const toTimestamp = (date: string) => new Date(date).getTime();

const getLatestSignature = (signatures: ReportSignature[]) => {
  return signatures.reduce((latest, current) => {
    const latestTs = toTimestamp(latest.updatedAt || latest.createdAt);
    const currentTs = toTimestamp(current.updatedAt || current.createdAt);
    return currentTs > latestTs ? current : latest;
  });
};

export const getSignatureByType = (
  signatures: ReportSignature[],
  signType: SignatureType
): ReportSignature | undefined => {
  const typedSignatures = signatures.filter((signature) => signature.signType === signType);
  if (!typedSignatures.length) return undefined;
  return getLatestSignature(typedSignatures);
};

export const getSignatureFlow = (signatures: ReportSignature[]): SignatureFlow => {
  const hasTwoSignType = signatures.some(
    (signature) =>
      signature.signType === 'non_practitioner_sign' || signature.signType === 'second_sign'
  );

  return hasTwoSignType ? 'two_sign' : 'one_sign';
};

export const getReviewerSignature = (signatures: ReportSignature[]): ReportSignature | undefined => {
  return signatures.find(
    (signature) =>
      signature.signType === 'pending_review'
  );
};

export const getDisplaySignatures = (signatures: ReportSignature[]): ReportSignature[] => {
  const flow = getSignatureFlow(signatures);

  if (flow === 'one_sign') {
    return [getSignatureByType(signatures, 'first_sign')].filter(
      (signature): signature is ReportSignature => Boolean(signature)
    );
  }

  return [
    getSignatureByType(signatures, 'non_practitioner_sign'),
    getSignatureByType(signatures, 'second_sign')
  ].filter((signature): signature is ReportSignature => Boolean(signature));
};

export const getFinalSignature = (signatures: ReportSignature[]): ReportSignature | undefined => {
  return getDisplaySignatures(signatures)
    .sort((a, b) => toTimestamp(a.createdAt) - toTimestamp(b.createdAt))
    .at(-1);
};
